import versionData from "./version.json";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { store, persistor } from "./redux/store";
import { Provider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { ThemeProvider } from "./hooks/ThemeProvider";
import CurrentOffers from "./components/CurrentOffers";
import PxcSearchPortal from "./portals/PxcSearchPortal";
import "./i18n/i18n";
import Preloader from "./components/Preloader";
import Portal from "./components/Portal";
import "./components/VehicleAccessories/VehicleAccessories.css";
import VehicleAccessoriesPortal from "./components/VehicleAccessoriesPortal/VehicleAccessoriesPortal";
import VehicleOffersPortal from "./components/VehicleOffersPortal/VehicleOffersPortal";
import ImageTooltipSetup from "./components/AccessibilitySetup/ImageTooltipSetup";
import ScreenReaderSetup from "./components/AccessibilitySetup/ScreenReaderSetup";
import {
  setupAccessibilitySync,
  injectAccessibilityStyles,
  injectStylesheets,
  loadCSS,
} from "./utils/shadowUtils";

const { APP_VERSION } = versionData;
const REACT_APP_URL = process.env.REACT_APP_URL;
const NODE_ENV = process.env.NODE_ENV;

// Clear local storage if app version changes
if (localStorage?.getItem("app_version") !== APP_VERSION) {
  localStorage?.clear();
  sessionStorage?.clear();
  localStorage?.setItem("app_version", APP_VERSION);
}

// Shared render logic
const createShadowHost = (parent, injectCSS = true) => {
  const shadowRoot = parent.attachShadow({ mode: "open" });
  injectStylesheets(shadowRoot);
  injectAccessibilityStyles(shadowRoot);

  const shadowWrapper = document.createElement("div");
  setupAccessibilitySync(shadowWrapper);
  shadowRoot.appendChild(shadowWrapper);

  if (injectCSS) {
    if (NODE_ENV !== "development") {
      loadCSS(shadowRoot, REACT_APP_URL);
    } else {
      const inlineStyle = document.querySelector("style")?.textContent;
      const devStyle = document.createElement("style");
      devStyle.textContent = inlineStyle;
      shadowRoot.appendChild(devStyle);
    }
  }

  return { shadowRoot, shadowWrapper };
};

const wrapWithProviders = (children) => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <Router>
        <ThemeProvider>
          <Suspense fallback={<Preloader />}>{children}</Suspense>
        </ThemeProvider>
      </Router>
    </PersistGate>
  </Provider>
);

// Renders a full React app inside Shadow DOM
const renderApp = async (containerId, Component) => {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.className =
    "text-[var(--text-dark-mode)] bg-[var(--whitesmoke-color)] pxc-accessories-wrapper";
  if (containerId === "am-marketplace") {
    const mainContainer = document.createElement("div");
    const suggestedContainer = document.createElement("div");
    const accessoriesContainer = document.createElement("div");

    mainContainer.id = "main-container";
    suggestedContainer.id = "suggested-container";
    accessoriesContainer.id = "vehicle-accessories-container-container";

    container.append(mainContainer, accessoriesContainer, suggestedContainer);

    const { shadowRoot, shadowWrapper } = createShadowHost(mainContainer);

    // Custom client-side routing
    window.addEventListener("routeChange", (event) => {
      const path = event.detail;
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    });

    const root = ReactDOM.createRoot(shadowWrapper);
    root.render(
      wrapWithProviders(
        <>
          <Component root={shadowRoot} />
          <ImageTooltipSetup shadowRoot={shadowRoot} />
          <ScreenReaderSetup shadowRoot={shadowRoot} />
        </>,
      ),
    );
  } else {
    const root = ReactDOM.createRoot(container);
    root.render(wrapWithProviders(<Component />));
  }
};

// Renders portals like VehicleOffersPortal and VehicleAccessoriesPortal
const renderPortal = (containerId, Component, identifier, clsName) => {
  const container = document.getElementById(containerId);
  if (!container) return;

  const { shadowRoot, shadowWrapper } = createShadowHost(container);
  const root = ReactDOM.createRoot(shadowWrapper);

  root.render(
    <Portal domNode={shadowWrapper} identifier={identifier} clsName={clsName}>
      {wrapWithProviders(
        <>
          <Component />
          <ImageTooltipSetup shadowRoot={shadowRoot} />
          <ScreenReaderSetup shadowRoot={shadowRoot} />
        </>,
      )}
    </Portal>,
  );
};

// Renders a non-shadow DOM portal (e.g., for Accessories Portal)
const renderPlainPortal = (containerId, Component, identifier, clsName) => {
  const container = document.getElementById(containerId);
  if (!container) return;

  const root = ReactDOM.createRoot(container);
  root.render(
    <Portal domNode={container} identifier={identifier} clsName={clsName}>
      {wrapWithProviders(<Component />)}
    </Portal>,
  );
};

// Run all renders
await renderApp("am-marketplace", App).then(() => {
  renderPortal(
    "suggested-container",
    VehicleOffersPortal,
    "suggested-container-portal",
    "pxc-portal text-[var(--text-dark-mode)] bg-[var(--whitesmoke-color)]",
  );
  renderPlainPortal(
    "vehicle-accessories-container-container",
    VehicleAccessoriesPortal,
    "vehicle-accessories-portal",
    "pxc-accessories-wrapper",
  );
});

renderApp("filter-root", PxcSearchPortal);
renderPortal(
  "promotion-root",
  CurrentOffers,
  "promotion-widget",
  "promotion-widget-wrapper bg-[var(--whitesmoke-color)]",
);

reportWebVitals();
